import { defineStore } from 'pinia';
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAsyncState } from '@vueuse/core';
import { PropertyService } from '@/http-services/property.service';
import { IPropertyContactInfo, Property } from '@ncss/models/dist/v3';
import { useAuth } from './auth.store';
import { ChangeResult, UpdateBuilding } from 'common';
import { HttpServiceResult } from '@/http-services/http.service';
import { useUser } from './user.store';

export const useSelectedProperty = defineStore('selected-property', () => {
  const propertyService = new PropertyService();
  const route = useRoute();
  const auth = useAuth();
  const propertyId = computed(() => route.params.propertyId as string);
  const user = useUser();

  const {
    state: property,
    isLoading,
    error,
    execute: refresh,
  } = useAsyncState(
    async () => {
      let p: Property | undefined = undefined;
      if (propertyId.value) {
        p = await propertyService.findById(propertyId.value);
      }
      return p;
    },
    undefined,
    { resetOnExecute: false }
  );
  watch(propertyId, (id, old) => {
    if (id !== old) {
      refresh();
      if (id) {
        user.viewedProperty(id);
      }
    }
  });

  async function deleteProperty() {
    if (property.value?._id) {
      const res = await propertyService.delete(property.value._id);
      return res;
    } else {
      return { success: false, error: 'Unable to delete property (no propertyId)' };
    }
  }

  async function updateContactInfo(contactInfo: IPropertyContactInfo): Promise<HttpServiceResult> {
    if (property.value?._id) {
      await propertyService.updateContactInfo(property.value._id, contactInfo);
      await refresh();
      return { success: true, data: null };
    } else {
      return { success: false, error: 'Unable to update property without a propertyId' };
    }
  }

  async function setCellularEnabled(enabled: boolean): Promise<HttpServiceResult> {
    if (property.value?._id) {
      await propertyService.setCellularEnabled(property.value._id, enabled);
      await refresh();
      return { success: true, data: null };
    } else {
      return {
        success: false,
        error: `Unable to ${enabled ? 'enable' : 'disable'} cellular without a selected property (no propertyId)`,
      };
    }
  }

  async function addComment(text: string) {
    if (property.value?._id) {
      await propertyService.addComment(property.value._id, text);
      await refresh();
    }
  }

  async function removeComment(commentId: string) {
    if (property.value?._id) {
      await propertyService.removeComment(property.value._id, commentId);
      await refresh();
    }
  }

  async function addGatewayComment(text: string) {
    if (property.value?._id) {
      await propertyService.addGatewayComment(property.value._id, text);
      await refresh();
    }
  }

  async function removeGatewayComment(commentId: string) {
    if (property.value?._id) {
      await propertyService.removeGatewayComment(property.value._id, commentId);
      await refresh();
    }
  }

  async function addGateway(gatewayId: number): Promise<HttpServiceResult> {
    if (property.value?._id) {
      const result = await propertyService.addGateway(property.value._id, gatewayId);
      if (result.success) {
        await refresh();
      }
      return result;
    }
    return {
      success: false,
      error: `Cannot program Gateway without a selected property (no propertyId)`,
    };
  }

  async function removeGateway(): Promise<HttpServiceResult> {
    if (property.value?._id) {
      const result = await propertyService.removeGateway(property.value._id);
      if (result.success) {
        await refresh();
      }
      return result;
    }
    return {
      success: false,
      error: `Cannot remove Gateway without a selected property (no propertyId)`,
    };
  }

  async function saveGatewayLocation(location: { lat: number; long: number }): Promise<HttpServiceResult> {
    if (property.value?._id) {
      const result = await propertyService.saveGatewayLocation(property.value._id, location);
      if (result.success) {
        await refresh();
      }
      return result;
    }
    return {
      success: false,
      error: `Cannot save Gateway location without a selected property (no propertyId)`,
    };
  }

  async function addRepeater(repeaterId: number): Promise<HttpServiceResult> {
    if (property.value?._id) {
      const result = await propertyService.addRepeater(property.value._id, repeaterId);
      if (result.success) {
        await refresh();
      }
      return result;
    }
    return {
      success: false,
      error: `Cannot program repeater without a selected property (no propertyId)`,
    };
  }

  async function removeRepeater(repeaterId: number): Promise<HttpServiceResult> {
    if (property.value?._id) {
      const result = await propertyService.removeRepeater(property.value._id, repeaterId);
      if (result.success) {
        await refresh();
      }
      return result;
    }
    return {
      success: false,
      error: `Cannot remove repeater without a selected property (no propertyId)`,
    };
  }

  async function saveRepeaterLocation(repeaterId: number, location: { lat: number; long: number }) {
    if (property.value?._id) {
      const result = await propertyService.saveRepeaterLocation(property.value._id, repeaterId, location);
      if (result.success) {
        await refresh();
      }
      return result;
    }
    return {
      success: false,
      error: `Cannot update repeater without a selected property (no propertyId)`,
    };
  }

  async function addRepeaterComment(repeaterId: number, text: string) {
    if (property.value?._id) {
      await propertyService.addRepeaterComment(property.value._id, repeaterId, text);
      await refresh();
    }
  }

  async function removeRepeaterComment(repeaterId: number, commentId: string) {
    if (property.value?._id) {
      await propertyService.removeRepeaterComment(property.value._id, repeaterId, commentId);
      await refresh();
    }
  }

  async function addPhoto(base64: string) {
    if (auth.user?._id && base64 && propertyId.value) {
      const name = `${auth.user._id}-photo-${Date.now()}.jpg`;
      await propertyService.addPhoto({ base64, name, context: { propertyId: propertyId.value } });
      await refresh();
    }
  }

  async function addGatewayPhoto(base64: string) {
    if (property.value?.gateway?.id && base64 && propertyId.value) {
      const name = `${property.value.gateway.id.toString(16).toUpperCase()}-${Date.now()}.jpg`;
      await propertyService.addGatewayPhoto({ base64, name, context: { propertyId: propertyId.value } });
      await refresh();
    }
  }

  async function addRepeaterPhoto(repeaterId: number, base64: string) {
    if (base64 && propertyId.value) {
      const name = `${repeaterId.toString(16).toUpperCase()}-${Date.now()}.jpg`;
      await propertyService.addRepeaterPhoto(repeaterId, { base64, name, context: { propertyId: propertyId.value } });
      await refresh();
    }
  }

  async function removeDocument(docName: string) {
    if (docName && propertyId.value) {
      await propertyService.removeDocument(propertyId.value, docName);
      await refresh();
    }
  }

  async function addDocument(name: string, base64: string) {
    if (auth.user?._id && base64 && propertyId.value) {
      await propertyService.addDocument({
        base64,
        name,
        context: {
          propertyId: propertyId.value,
        },
      });
      await refresh();
    }
  }

  async function updateBuilding(name: string, update: UpdateBuilding): Promise<HttpServiceResult<ChangeResult>> {
    if (auth.user?._id && propertyId.value && name) {
      const res = await propertyService.updateBuilding(propertyId.value, name, update);
      if (res.success) {
        await refresh();
      }
      return res;
    }
    return {
      success: false,
      error: 'Cannot update property building without a selected property, user, and building name',
    };
  }

  async function deleteBuilding(name: string): Promise<HttpServiceResult> {
    if (auth.user?._id && propertyId.value && name) {
      const res = await propertyService.deleteBuilding(propertyId.value, name);
      if (res.success) {
        await refresh();
      }
      return res;
    }
    return {
      success: false,
      error: 'Cannot delete property building without a selected property, user, and building name',
    };
  }

  async function addBuilding(name: string): Promise<HttpServiceResult<ChangeResult>> {
    if (auth.user?._id && propertyId.value && name) {
      const res = await propertyService.addBuilding(propertyId.value, name);
      if (res.success) {
        await refresh();
      }
      return res;
    }
    return {
      success: false,
      error: 'Cannot create property building without a selected property, user, and building name',
    };
  }

  async function updateGatewayDescription(description: string) {
    if (!propertyId.value) {
      throw new Error('Cannot update Gateway Description without a selected property (no propertyId)');
    }
    await propertyService.updateGatewayDescription(propertyId.value, description);
    await refresh();
  }

  async function updateRepeaterDescription(repeaterId: number, description: string) {
    if (!propertyId.value) {
      throw new Error('Cannot update Gateway Description without a selected property (no propertyId)');
    }
    await propertyService.updateRepeaterDescription(propertyId.value, repeaterId, description);
    await refresh();
  }

  return {
    propertyId,
    isLoading,
    property,
    error,
    refresh,
    addComment,
    removeComment,
    addGatewayComment,
    removeGatewayComment,
    addRepeaterComment,
    removeRepeaterComment,
    addPhoto,
    addGatewayPhoto,
    addRepeaterPhoto,
    removeDocument,
    addDocument,
    updateGatewayDescription,
    updateRepeaterDescription,
    updateBuilding,
    addBuilding,
    deleteProperty,
    updateContactInfo,
    setCellularEnabled,
    deleteBuilding,
    addRepeater,
    removeRepeater,
    saveRepeaterLocation,
    addGateway,
    removeGateway,
    saveGatewayLocation,
  };
});
